import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  Upload,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  Euro,
  FolderOpen,
  Download,
  Eye,
  Clock,
  AlertTriangle,
  FileWarning,
  GraduationCap,
  Shield,
  X,
  ExternalLink,
  Copy,
  CheckCircle2,
  Building2,
  ClipboardCheck,
  UserPlus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LogOut,
  FilePlus2
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { formatDate, formatDateShort, formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Mensualisation légale française : 52 semaines / 12 mois (35h -> 151,67 h/mois)
const WEEKS_PER_MONTH = 52 / 12;
const WEEKS_PER_MONTH_LABEL = WEEKS_PER_MONTH.toFixed(4).replace('.', ',');

// Types de contrat exclus de la préparation de paie (pas de salaire géré en interne)
const TYPES_CONTRAT_HORS_PAIE = ['Interim', 'Prestataire'];

// Motifs de fin de contrat (sortie des effectifs)
const MOTIFS_FIN_CONTRAT = [
  { value: 'demission', label: 'Démission' },
  { value: 'licenciement', label: 'Licenciement' },
  { value: 'fin_cdd', label: 'Fin de CDD' },
  { value: 'rupture_conventionnelle', label: 'Rupture conventionnelle' },
  { value: 'retraite', label: 'Retraite' },
  { value: 'fin_periode_essai', label: "Fin de période d'essai" },
  { value: 'mutation', label: 'Mutation' },
  { value: 'autre', label: 'Autre' }
];
const getMotifFinLabel = (code) => MOTIFS_FIN_CONTRAT.find(m => m.value === code)?.label || code || '';

// Motifs de création d'un nouveau contrat (sans perte de l'historique)
const MOTIFS_NOUVEAU_CONTRAT = [
  { value: 'renouvellement_cdd', label: 'Renouvellement de CDD' },
  { value: 'passage_cdi', label: 'Passage en CDI' },
  { value: 'changement_type', label: 'Changement de type de contrat' },
  { value: 'autre', label: 'Autre' }
];

// Tri par défaut de la liste (persisté dans localStorage)
const SORT_STORAGE_KEY = 'employees_sort';
const DEFAULT_SORT = { column: 'nom', direction: 'asc' };
const loadStoredSort = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY) || 'null');
    if (stored && ['nom', 'prenom'].includes(stored.column) && ['asc', 'desc'].includes(stored.direction)) {
      return stored;
    }
  } catch {
    // localStorage indisponible : tri par défaut
  }
  return DEFAULT_SORT;
};

// Ramène les valeurs historiques (M, F, Masculin, Homme...) aux valeurs du sélecteur
const normalizeSexe = (value) => {
  if (!value) return '';
  const v = String(value).trim().toLowerCase();
  if (['homme', 'masculin', 'm', 'h'].includes(v)) return 'homme';
  if (['femme', 'feminin', 'féminin', 'f'].includes(v)) return 'femme';
  return v;
};

// Liste complète des nationalités
const ALL_NATIONALITIES = [
  'Française', 'Afghane', 'Albanaise', 'Algérienne', 'Allemande', 'Américaine', 'Andorrane',
  'Angolaise', 'Argentine', 'Arménienne', 'Australienne', 'Autrichienne', 'Azerbaïdjanaise',
  'Bahreïnienne', 'Bangladaise', 'Belge', 'Béninoise', 'Bhoutanaise', 'Biélorusse',
  'Birmane', 'Bolivienne', 'Bosnienne', 'Botswanaise', 'Brésilienne', 'Britannique',
  'Brunéienne', 'Bulgare', 'Burkinabè', 'Burundaise', 'Cambodgienne', 'Camerounaise',
  'Canadienne', 'Cap-Verdienne', 'Centrafricaine', 'Chilienne', 'Chinoise', 'Chypriote',
  'Colombienne', 'Comorienne', 'Congolaise', 'Costaricaine', 'Croate', 'Cubaine',
  'Danoise', 'Djiboutienne', 'Dominicaine', 'Dominiquaise', 'Égyptienne', 'Émirienne',
  'Équatorienne', 'Érythréenne', 'Espagnole', 'Estonienne', 'Éthiopienne', 'Fidjienne',
  'Finlandaise', 'Gabonaise', 'Gambienne', 'Géorgienne', 'Ghanéenne', 'Grecque',
  'Guatémaltèque', 'Guinéenne', 'Haïtienne', 'Hondurienne', 'Hongroise', 'Indienne',
  'Indonésienne', 'Irakienne', 'Iranienne', 'Irlandaise', 'Islandaise', 'Israélienne',
  'Italienne', 'Ivoirienne', 'Jamaïcaine', 'Japonaise', 'Jordanienne', 'Kazakhe',
  'Kényane', 'Kirghize', 'Koweïtienne', 'Laotienne', 'Lesothane', 'Lettone',
  'Libanaise', 'Libérienne', 'Libyenne', 'Lituanienne', 'Luxembourgeoise', 'Macédonienne',
  'Malgache', 'Malaisienne', 'Malawienne', 'Maldivienne', 'Malienne', 'Maltaise',
  'Marocaine', 'Mauricienne', 'Mauritanienne', 'Mexicaine', 'Moldave', 'Monégasque',
  'Mongole', 'Monténégrine', 'Mozambicaine', 'Namibienne', 'Néerlandaise', 'Néo-Zélandaise',
  'Népalaise', 'Nicaraguayenne', 'Nigériane', 'Nigérienne', 'Norvégienne', 'Omanaise',
  'Ougandaise', 'Ouzbèke', 'Pakistanaise', 'Palestinienne', 'Panaméenne', 'Paraguayenne',
  'Péruvienne', 'Philippine', 'Polonaise', 'Portugaise', 'Qatarienne', 'Roumaine',
  'Russe', 'Rwandaise', 'Saoudienne', 'Sénégalaise', 'Serbe', 'Sierra-Léonaise',
  'Singapourienne', 'Slovaque', 'Slovène', 'Somalienne', 'Soudanaise', 'Sri-Lankaise',
  'Sud-Africaine', 'Sud-Coréenne', 'Suédoise', 'Suisse', 'Surinamaise', 'Swazie',
  'Syrienne', 'Tadjike', 'Tanzanienne', 'Tchadienne', 'Tchèque', 'Thaïlandaise',
  'Togolaise', 'Tongienne', 'Trinidadienne', 'Tunisienne', 'Turkmène', 'Turque',
  'Ukrainienne', 'Uruguayenne', 'Vénézuélienne', 'Vietnamienne', 'Yéménite',
  'Zambienne', 'Zimbabwéenne', 'Autre'
];

// Liste des pays
const ALL_COUNTRIES = [
  'France', 'Afghanistan', 'Albanie', 'Algérie', 'Allemagne', 'Andorre', 'Angola',
  'Argentine', 'Arménie', 'Australie', 'Autriche', 'Azerbaïdjan', 'Bahreïn', 'Bangladesh',
  'Belgique', 'Bénin', 'Bhoutan', 'Biélorussie', 'Birmanie', 'Bolivie', 'Bosnie-Herzégovine',
  'Botswana', 'Brésil', 'Brunei', 'Bulgarie', 'Burkina Faso', 'Burundi', 'Cambodge',
  'Cameroun', 'Canada', 'Cap-Vert', 'Centrafrique', 'Chili', 'Chine', 'Chypre', 'Colombie',
  'Comores', 'Congo', 'Costa Rica', 'Côte d\'Ivoire', 'Croatie', 'Cuba', 'Danemark',
  'Djibouti', 'Dominique', 'Égypte', 'Émirats arabes unis', 'Équateur', 'Érythrée',
  'Espagne', 'Estonie', 'États-Unis', 'Éthiopie', 'Fidji', 'Finlande', 'Gabon', 'Gambie',
  'Géorgie', 'Ghana', 'Grèce', 'Guatemala', 'Guinée', 'Haïti', 'Honduras', 'Hongrie',
  'Inde', 'Indonésie', 'Irak', 'Iran', 'Irlande', 'Islande', 'Israël', 'Italie',
  'Jamaïque', 'Japon', 'Jordanie', 'Kazakhstan', 'Kenya', 'Kirghizistan', 'Koweït',
  'Laos', 'Lesotho', 'Lettonie', 'Liban', 'Liberia', 'Libye', 'Lituanie', 'Luxembourg',
  'Macédoine du Nord', 'Madagascar', 'Malaisie', 'Malawi', 'Maldives', 'Mali', 'Malte',
  'Maroc', 'Maurice', 'Mauritanie', 'Mexique', 'Moldavie', 'Monaco', 'Mongolie',
  'Monténégro', 'Mozambique', 'Namibie', 'Népal', 'Nicaragua', 'Niger', 'Nigeria',
  'Norvège', 'Nouvelle-Zélande', 'Oman', 'Ouganda', 'Ouzbékistan', 'Pakistan', 'Palestine',
  'Panama', 'Paraguay', 'Pays-Bas', 'Pérou', 'Philippines', 'Pologne', 'Portugal', 'Qatar',
  'République dominicaine', 'République tchèque', 'Roumanie', 'Royaume-Uni', 'Russie',
  'Rwanda', 'Arabie saoudite', 'Sénégal', 'Serbie', 'Sierra Leone', 'Singapour',
  'Slovaquie', 'Slovénie', 'Somalie', 'Soudan', 'Sri Lanka', 'Afrique du Sud',
  'Corée du Sud', 'Suède', 'Suisse', 'Suriname', 'Eswatini', 'Syrie', 'Tadjikistan',
  'Tanzanie', 'Tchad', 'Thaïlande', 'Togo', 'Tonga', 'Trinité-et-Tobago', 'Tunisie',
  'Turkménistan', 'Turquie', 'Ukraine', 'Uruguay', 'Venezuela', 'Vietnam', 'Yémen',
  'Zambie', 'Zimbabwe', 'Autre'
];

const DEFAULT_CATEGORY_ICONS = {
  contrat: { icon: FileText, color: 'text-blue-500' },
  avenant: { icon: FileText, color: 'text-indigo-500' },
  arret_maladie: { icon: AlertTriangle, color: 'text-red-500' },
  disciplinaire: { icon: FileWarning, color: 'text-amber-500' },
  formation: { icon: GraduationCap, color: 'text-emerald-500' },
  administratif: { icon: Shield, color: 'text-slate-500' },
  autre: { icon: FolderOpen, color: 'text-slate-400' },
};

const Employees = () => {
  const { user, hasPermission } = useAuth();
  // Le salaire n'est visible que dans la fiche, et uniquement avec la permission adéquate
  const canViewSalary = ['admin', 'rh', 'comptabilite', 'super_admin'].includes(user?.role) || hasPermission('payroll_view');
  const canEditEmployees = ['admin', 'rh', 'super_admin'].includes(user?.role) || hasPermission('employees_edit');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  const [dossierData, setDossierData] = useState(null);
  const [dossierEmployee, setDossierEmployee] = useState(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [departments, setDepartments] = useState({});
  // DPAE Modal states
  const [isDpaeDialogOpen, setIsDpaeDialogOpen] = useState(false);
  const [newlyCreatedEmployee, setNewlyCreatedEmployee] = useState(null);
  const [structureInfo, setStructureInfo] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [urssafConfig, setUrssafConfig] = useState(null);
  const [submittingDpae, setSubmittingDpae] = useState(false);
  const [heureEmbauche, setHeureEmbauche] = useState('08:00');
  const [documentCategories, setDocumentCategories] = useState([]);
  const [contratHistory, setContratHistory] = useState([]);
  const [isAvenantDialogOpen, setIsAvenantDialogOpen] = useState(false);
  // Sortie des effectifs
  const [isSortieDialogOpen, setIsSortieDialogOpen] = useState(false);
  const [sortieEmployee, setSortieEmployee] = useState(null);
  const [sortieData, setSortieData] = useState({ date_sortie: '', motif_fin_contrat: '', commentaire: '' });
  const [submittingSortie, setSubmittingSortie] = useState(false);
  // Nouveau contrat (renouvellement de CDD, passage en CDI...)
  const [isNewContratDialogOpen, setIsNewContratDialogOpen] = useState(false);
  const [newContratData, setNewContratData] = useState({
    type_contrat: 'CDI', date_debut: '', date_fin: '', poste: '', departement: '',
    duree_hebdo: 35, salaire_base: '', taux_horaire: '', motif_creation: ''
  });
  const [avenantData, setAvenantData] = useState({
    type_contrat: '',
    date_debut: '',
    date_fin: '',
    poste: '',
    departement: '',
    duree_hebdo: 35,
    salaire_base: '',
    taux_horaire: '',
    motif_avenant: '',
    niveau: '',
    echelon: '',
    statut_professionnel: ''
  });
  const [structureConfig, setStructureConfig] = useState({
    taux_majoration_heures_sup_35_39: 10,
    taux_majoration_heures_sup_25: 25,
    taux_majoration_heures_sup_50: 50
  });

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    sexe: '',
    date_naissance: '',
    lieu_naissance: '',
    pays_naissance: 'France',
    nationalite: 'Française',
    // Titre de séjour
    soumis_titre_sejour: false,
    numero_titre_sejour: '',
    date_expiration_titre_sejour: '',
    // Nouveaux champs identité
    numero_secu: '',
    cle_secu: '',
    iban: '',
    bic: '',
    adresse: '',
    complement_adresse: '',
    code_postal: '',
    ville: '',
    situation_familiale: '',
    nb_enfants: 0,
    personne_urgence: '',
    tel_urgence: '',
    contact_urgence_affiliation: '',
    poste: '',
    departement: '',
    date_embauche: '',
    type_contrat: 'CDI',
    date_fin_contrat: '',
    sans_contrat: false,
    // Options paie
    droit_paniers_repas: true,
    exclure_preparation_paie: false,
    // Fin de contrat
    motif_fin_contrat: '',
    date_fin_contrat_effective: '',
    // Matricules
    matricule: '',
    matricule_paie: '',
    // Nouveaux champs contrat
    niveau: '',
    echelon: '',
    statut_professionnel: '',
    classification: '',
    coefficient: '',
    periode_essai_debut: '',
    periode_essai_fin: '',
    periode_essai_prolongation: '',
    periode_essai_duree_mois: '',
    convention_collective: '',
    duree_hebdo: 35,
    taux_horaire: '',
    salaire_base: '',
    statut: 'actif',
    creer_compte: false
  });

  // Filtres supplémentaires
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterContrat, setFilterContrat] = useState('all');
  // Tri colonnes ('nom' ou 'prenom', 'asc' ou 'desc') - alphabétique par défaut, persisté
  const [sortColumn, setSortColumn] = useState(() => loadStoredSort().column);
  const [sortDirection, setSortDirection] = useState(() => loadStoredSort().direction);
  // Document editing
  const [editingDocument, setEditingDocument] = useState(null);
  const [isEditDocDialogOpen, setIsEditDocDialogOpen] = useState(false);
  // Aperçu document (dialog avec iframe/image)
  const [previewDoc, setPreviewDoc] = useState(null);

  const [uploadData, setUploadData] = useState({
    categorie: 'autre',
    titre: '',
    description: '',
    date_document: '',
    file: null
  });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchStructureInfo();
    fetchUrssafConfig();
    fetchDocumentCategories();
    fetchStructureConfig();
  }, []);

  const fetchDocumentCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/document-categories`);
      const cats = Object.entries(response.data).map(([code, info]) => {
        const defaultIcon = DEFAULT_CATEGORY_ICONS[code] || { icon: FolderOpen, color: 'text-slate-400' };
        return { value: code, label: info.label || code, icon: defaultIcon.icon, color: defaultIcon.color, hexColor: info.color };
      });
      setDocumentCategories(cats);
    } catch (error) {
      // Fallback aux catégories par défaut
      setDocumentCategories([
        { value: 'contrat', label: 'Contrat', icon: FileText, color: 'text-blue-500' },
        { value: 'avenant', label: 'Avenant', icon: FileText, color: 'text-indigo-500' },
        { value: 'arret_maladie', label: 'Arrêt maladie', icon: AlertTriangle, color: 'text-red-500' },
        { value: 'disciplinaire', label: 'Disciplinaire', icon: FileWarning, color: 'text-amber-500' },
        { value: 'formation', label: 'Formation', icon: GraduationCap, color: 'text-emerald-500' },
        { value: 'administratif', label: 'Administratif', icon: Shield, color: 'text-slate-500' },
        { value: 'autre', label: 'Autre', icon: FolderOpen, color: 'text-slate-400' },
      ]);
    }
  };

  const fetchStructureConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/structure/config`);
      if (response.data) {
        setStructureConfig({
          taux_majoration_heures_sup_35_39: response.data.taux_majoration_heures_sup_35_39 ?? 10,
          taux_majoration_heures_sup_25: response.data.taux_majoration_heures_sup_25 ?? 25,
          taux_majoration_heures_sup_50: response.data.taux_majoration_heures_sup_50 ?? 50
        });
      }
    } catch (error) {
      console.error('Error fetching structure config:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/departments`);
      setDepartments(response.data || {});
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchStructureInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      if (response.data?.structure) {
        setStructureInfo(response.data.structure);
      }
    } catch (error) {
      console.error('Error fetching structure info:', error);
    }
  };

  const fetchUrssafConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/urssaf-config`);
      setUrssafConfig(response.data);
    } catch (error) {
      console.error('Error fetching URSSAF config:', error);
      setUrssafConfig({ configured: false });
    }
  };

  const submitDpaeToUrssaf = async () => {
    if (!newlyCreatedEmployee) return;

    setSubmittingDpae(true);
    try {
      const response = await axios.post(`${API_URL}/dpae/submit`, {
        employee_id: newlyCreatedEmployee.id,
        heure_embauche: heureEmbauche
      });

      if (response.data.success) {
        toast.success(`DPAE soumise avec succes! Reference: ${response.data.reference || 'En cours'}`);
        setIsDpaeDialogOpen(false);
      } else {
        toast.error(response.data.message || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Error submitting DPAE:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la soumission de la DPAE';
      toast.error(message);
    } finally {
      setSubmittingDpae(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  // Calcule les heures effectives mensuelles en tenant compte des majorations
  // Ex: 39h/sem = 35h base + 4h à 110% = heures effectives de 39.4 (si majoration 10%)
  const calculateEffectiveHours = (heures) => {
    const h = parseFloat(heures);
    if (!h || h <= 35) return h || 0;

    const rate35_39 = (structureConfig.taux_majoration_heures_sup_35_39 || 10) / 100;
    const rate39_43 = (structureConfig.taux_majoration_heures_sup_25 || 25) / 100;
    const rate43plus = (structureConfig.taux_majoration_heures_sup_50 || 50) / 100;

    let effective = 35;
    if (h > 35) effective += (Math.min(h, 39) - 35) * (1 + rate35_39);
    if (h > 39) effective += (Math.min(h, 43) - 39) * (1 + rate39_43);
    if (h > 43) effective += (h - 43) * (1 + rate43plus);

    return effective;
  };

  // Salaire mensuel = taux_horaire_base × heures_effectives × 52/12 (151,67 h pour 35h)
  // Le salaire INCLUT les majorations pour les heures > 35h
  const calculateSalary = (taux, heures) => {
    if (taux && heures) {
      const effectiveHours = calculateEffectiveHours(heures);
      return Math.round(parseFloat(taux) * effectiveHours * WEEKS_PER_MONTH);
    }
    return '';
  };

  // Retrouve le taux horaire de base à partir du salaire total (majorations incluses)
  const calculateTaux = (salaire, heures) => {
    if (salaire && heures) {
      const effectiveHours = calculateEffectiveHours(heures);
      return (parseFloat(salaire) / (effectiveHours * WEEKS_PER_MONTH)).toFixed(2);
    }
    return '';
  };

  // Applique le calcul automatique salaire <-> taux horaire à n'importe quel état de formulaire
  const applySalaryField = (data, field, value) => {
    if (field === 'taux_horaire') {
      return { ...data, taux_horaire: value, salaire_base: calculateSalary(value, data.duree_hebdo) };
    }
    if (field === 'salaire_base') {
      return { ...data, salaire_base: value, taux_horaire: calculateTaux(value, data.duree_hebdo) };
    }
    if (field === 'duree_hebdo') {
      const updates = { duree_hebdo: value };
      if (data.taux_horaire) {
        updates.salaire_base = calculateSalary(data.taux_horaire, value);
      } else if (data.salaire_base) {
        updates.taux_horaire = calculateTaux(data.salaire_base, value);
      }
      return { ...data, ...updates };
    }
    return { ...data, [field]: value };
  };

  const handleTauxChange = (value) => {
    const taux = value;
    const salaire = calculateSalary(taux, formData.duree_hebdo);
    setFormData({ ...formData, taux_horaire: taux, salaire_base: salaire });
  };

  const handleSalaireChange = (value) => {
    const salaire = value;
    const taux = calculateTaux(salaire, formData.duree_hebdo);
    setFormData({ ...formData, salaire_base: salaire, taux_horaire: taux });
  };

  const handleDureeChange = (value) => {
    const heures = value;
    let updates = { duree_hebdo: heures };
    if (formData.taux_horaire) {
      updates.salaire_base = calculateSalary(formData.taux_horaire, heures);
    } else if (formData.salaire_base) {
      updates.taux_horaire = calculateTaux(formData.salaire_base, heures);
    }
    setFormData({ ...formData, ...updates });
  };

  // Décompose le salaire mensuel (majorations incluses) en : base 35h + majorations par palier
  const calculateMajorations = (tauxHoraire, dureeHebdo) => {
    const taux = parseFloat(tauxHoraire);
    const heures = parseFloat(dureeHebdo);
    if (!taux || !heures || heures <= 35) return null;

    const rate35_39 = structureConfig.taux_majoration_heures_sup_35_39 / 100;
    const rate39_43 = structureConfig.taux_majoration_heures_sup_25 / 100;
    const rate43plus = structureConfig.taux_majoration_heures_sup_50 / 100;

    // Salaire de base pour 35h
    const salaireBase35h = 35 * taux * WEEKS_PER_MONTH;

    const tiers = [];

    // Palier 35-39h : heures payées au taux majoré
    if (heures > 35) {
      const tierHours = Math.min(heures, 39) - 35;
      const montantTotal = tierHours * taux * (1 + rate35_39) * WEEKS_PER_MONTH;
      const montantMajoration = tierHours * taux * rate35_39 * WEEKS_PER_MONTH;
      tiers.push({
        label: `35h → ${Math.min(heures, 39)}h`,
        hours: tierHours,
        ratePercent: structureConfig.taux_majoration_heures_sup_35_39,
        effectiveRate: taux * (1 + rate35_39),
        monthlyAmount: montantTotal,
        majorationAmount: montantMajoration
      });
    }

    // Palier 39-43h
    if (heures > 39) {
      const tierHours = Math.min(heures, 43) - 39;
      const montantTotal = tierHours * taux * (1 + rate39_43) * WEEKS_PER_MONTH;
      const montantMajoration = tierHours * taux * rate39_43 * WEEKS_PER_MONTH;
      tiers.push({
        label: `39h → ${Math.min(heures, 43)}h`,
        hours: tierHours,
        ratePercent: structureConfig.taux_majoration_heures_sup_25,
        effectiveRate: taux * (1 + rate39_43),
        monthlyAmount: montantTotal,
        majorationAmount: montantMajoration
      });
    }

    // Palier >43h
    if (heures > 43) {
      const tierHours = heures - 43;
      const montantTotal = tierHours * taux * (1 + rate43plus) * WEEKS_PER_MONTH;
      const montantMajoration = tierHours * taux * rate43plus * WEEKS_PER_MONTH;
      tiers.push({
        label: `43h → ${heures}h`,
        hours: tierHours,
        ratePercent: structureConfig.taux_majoration_heures_sup_50,
        effectiveRate: taux * (1 + rate43plus),
        monthlyAmount: montantTotal,
        majorationAmount: montantMajoration
      });
    }

    const totalMajorations = tiers.reduce((sum, t) => sum + t.majorationAmount, 0);
    const totalHeuresMajorees = tiers.reduce((sum, t) => sum + t.monthlyAmount, 0);

    return { tiers, totalMajorations, totalHeuresMajorees, salaireBase35h };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation des champs requis avec messages explicites
    const champsManquants = [];
    if (!formData.nom) champsManquants.push('Nom');
    if (!formData.prenom) champsManquants.push('Prénom');
    if (!formData.email) champsManquants.push('Email');
    if (!formData.date_naissance) champsManquants.push('Date de naissance');
    if (!formData.poste) champsManquants.push('Poste');
    if (!formData.departement) champsManquants.push('Département');
    if (!formData.date_embauche) champsManquants.push("Date d'embauche");
    if (!formData.sans_contrat && !formData.type_contrat) champsManquants.push('Type de contrat');
    if (champsManquants.length > 0) {
      toast.error(`Champs obligatoires manquants : ${champsManquants.join(', ')}`);
      return;
    }

    try {
      const horsPaie = TYPES_CONTRAT_HORS_PAIE.includes(formData.type_contrat);
      const dataToSend = {
        ...formData,
        // Intérim / prestataire : rémunération gérée par l'entreprise tierce, on efface le salaire
        taux_horaire: !horsPaie && formData.taux_horaire ? parseFloat(formData.taux_horaire) : null,
        salaire_base: !horsPaie && formData.salaire_base ? parseFloat(formData.salaire_base) : null,
        duree_hebdo: formData.duree_hebdo ? parseFloat(formData.duree_hebdo) : 35,
        nb_enfants: formData.nb_enfants ? parseInt(formData.nb_enfants) : 0,
        periode_essai_duree_mois: formData.periode_essai_duree_mois ? parseInt(formData.periode_essai_duree_mois) : null
      };
      if (!canViewSalary) {
        // Sans droit de lecture du salaire, ne jamais envoyer (ni effacer) la rémunération
        delete dataToSend.salaire_base;
        delete dataToSend.taux_horaire;
      }

      if (selectedEmployee) {
        await axios.put(`${API_URL}/employees/${selectedEmployee.id}`, dataToSend);
        toast.success('Employé modifié avec succès');
      } else {
        const response = await axios.post(`${API_URL}/employees`, dataToSend);
        toast.success('Employé créé avec succès');

        // Créer un compte utilisateur si demandé
        if (formData.creer_compte && response.data?.id) {
          try {
            const originUrl = window.location.origin;
            await axios.post(`${API_URL}/employees/${response.data.id}/create-account?origin_url=${encodeURIComponent(originUrl)}`);
            toast.success('Compte utilisateur créé - un email de confirmation a été envoyé');
          } catch (accountError) {
            console.error('Error creating account:', accountError);
            toast.error('Employé créé mais erreur lors de la création du compte utilisateur');
          }
        }

        // Proposer la DPAE pour le nouvel employe
        setNewlyCreatedEmployee(response.data);
        setIsDpaeDialogOpen(true);
      }

      setIsDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) return;
    
    try {
      await axios.delete(`${API_URL}/employees/${id}`);
      toast.success('Employé supprimé');
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      nom: employee.nom,
      prenom: employee.prenom,
      email: employee.email,
      telephone: employee.telephone || '',
      sexe: normalizeSexe(employee.sexe),
      date_naissance: employee.date_naissance,
      lieu_naissance: employee.lieu_naissance || '',
      pays_naissance: employee.pays_naissance || 'France',
      nationalite: employee.nationalite || 'Française',
      soumis_titre_sejour: employee.soumis_titre_sejour || false,
      numero_titre_sejour: employee.numero_titre_sejour || '',
      date_expiration_titre_sejour: employee.date_expiration_titre_sejour || '',
      numero_secu: employee.numero_secu || '',
      cle_secu: employee.cle_secu || '',
      iban: employee.iban || '',
      bic: employee.bic || '',
      adresse: employee.adresse || '',
      complement_adresse: employee.complement_adresse || '',
      code_postal: employee.code_postal || '',
      ville: employee.ville || '',
      situation_familiale: employee.situation_familiale || '',
      nb_enfants: employee.nb_enfants || 0,
      personne_urgence: employee.personne_urgence || '',
      tel_urgence: employee.tel_urgence || '',
      contact_urgence_affiliation: employee.contact_urgence_affiliation || '',
      poste: employee.poste,
      departement: employee.departement,
      date_embauche: employee.date_embauche,
      type_contrat: employee.type_contrat,
      date_fin_contrat: employee.date_fin_contrat || '',
      sans_contrat: employee.sans_contrat || false,
      droit_paniers_repas: employee.droit_paniers_repas !== false,
      exclure_preparation_paie: employee.exclure_preparation_paie || false,
      motif_fin_contrat: employee.motif_fin_contrat || '',
      date_fin_contrat_effective: employee.date_fin_contrat_effective || '',
      matricule: employee.matricule || '',
      matricule_paie: employee.matricule_paie || '',
      niveau: employee.niveau || '',
      echelon: employee.echelon || '',
      statut_professionnel: employee.statut_professionnel || '',
      classification: employee.classification || '',
      coefficient: employee.coefficient || '',
      periode_essai_debut: employee.periode_essai_debut || '',
      periode_essai_fin: employee.periode_essai_fin || '',
      periode_essai_prolongation: employee.periode_essai_prolongation || '',
      periode_essai_duree_mois: employee.periode_essai_duree_mois?.toString() || '',
      convention_collective: employee.convention_collective || '',
      duree_hebdo: employee.duree_hebdo || 35,
      taux_horaire: employee.taux_horaire?.toString() || '',
      salaire_base: employee.salaire_base?.toString() || '',
      statut: employee.statut
    });
    setIsDialogOpen(true);
  };

  const fetchContratHistory = async (employeeId) => {
    try {
      const response = await axios.get(`${API_URL}/employees/${employeeId}/contrats`);
      setContratHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching contrats:', error);
      setContratHistory([]);
    }
  };

  const handleCreateAvenant = async (e) => {
    e.preventDefault();
    if (!dossierEmployee) return;
    try {
      // Find current active contract
      const activeContrat = contratHistory.find(c => c.statut === 'actif');
      const payload = {
        employee_id: dossierEmployee.id,
        type_contrat: avenantData.type_contrat || dossierEmployee.type_contrat,
        date_debut: avenantData.date_debut,
        date_fin: avenantData.date_fin || null,
        poste: avenantData.poste || dossierEmployee.poste,
        departement: avenantData.departement || dossierEmployee.departement,
        duree_hebdo: parseFloat(avenantData.duree_hebdo) || dossierEmployee.duree_hebdo || 35,
        salaire_base: canViewSalary ? (parseFloat(avenantData.salaire_base) || dossierEmployee.salaire_base || null) : (dossierEmployee.salaire_base || null),
        taux_horaire: canViewSalary ? (parseFloat(avenantData.taux_horaire) || null) : null,
        niveau: avenantData.niveau || dossierEmployee.niveau || '',
        echelon: avenantData.echelon || dossierEmployee.echelon || '',
        statut_professionnel: avenantData.statut_professionnel || dossierEmployee.statut_professionnel || '',
        est_avenant: true,
        contrat_parent_id: activeContrat?.id || null,
        motif_avenant: avenantData.motif_avenant
      };
      await axios.post(`${API_URL}/employees/${dossierEmployee.id}/contrats`, payload);
      toast.success('Avenant créé avec succès');
      setIsAvenantDialogOpen(false);
      setAvenantData({
        type_contrat: '', date_debut: '', date_fin: '', poste: '', departement: '',
        duree_hebdo: 35, salaire_base: '', taux_horaire: '', motif_avenant: '',
        niveau: '', echelon: '', statut_professionnel: ''
      });
      fetchContratHistory(dossierEmployee.id);
      fetchEmployees();
    } catch (error) {
      console.error('Error creating avenant:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'avenant');
    }
  };

  // Nouveau contrat (renouvellement de CDD, passage en CDI...) : l'ancien contrat est clôturé
  // avec le motif choisi et reste dans l'historique
  const openNewContratDialog = () => {
    if (!dossierEmployee) return;
    setNewContratData({
      type_contrat: dossierEmployee.type_contrat === 'CDD' ? 'CDD' : 'CDI',
      date_debut: '',
      date_fin: '',
      poste: dossierEmployee.poste || '',
      departement: dossierEmployee.departement || '',
      duree_hebdo: dossierEmployee.duree_hebdo || 35,
      salaire_base: dossierEmployee.salaire_base || '',
      taux_horaire: dossierEmployee.taux_horaire || '',
      motif_creation: dossierEmployee.type_contrat === 'CDD' ? 'renouvellement_cdd' : ''
    });
    setIsNewContratDialogOpen(true);
  };

  const handleCreateNewContrat = async (e) => {
    e.preventDefault();
    if (!dossierEmployee) return;
    if (newContratData.type_contrat !== 'CDI' && !newContratData.date_fin) {
      toast.error('Date de fin obligatoire pour ce type de contrat');
      return;
    }
    const horsPaie = TYPES_CONTRAT_HORS_PAIE.includes(newContratData.type_contrat);
    try {
      const payload = {
        employee_id: dossierEmployee.id,
        type_contrat: newContratData.type_contrat,
        date_debut: newContratData.date_debut,
        date_fin: newContratData.type_contrat === 'CDI' ? null : (newContratData.date_fin || null),
        poste: newContratData.poste || dossierEmployee.poste,
        departement: newContratData.departement || dossierEmployee.departement,
        duree_hebdo: parseFloat(newContratData.duree_hebdo) || 35,
        salaire_base: horsPaie ? null : (canViewSalary ? (parseFloat(newContratData.salaire_base) || null) : (dossierEmployee.salaire_base || null)),
        taux_horaire: horsPaie || !canViewSalary ? null : (parseFloat(newContratData.taux_horaire) || null),
        niveau: dossierEmployee.niveau || '',
        echelon: dossierEmployee.echelon || '',
        statut_professionnel: dossierEmployee.statut_professionnel || '',
        est_avenant: false,
        motif_creation: newContratData.motif_creation || 'autre'
      };
      await axios.post(`${API_URL}/employees/${dossierEmployee.id}/contrats`, payload);
      toast.success('Nouveau contrat enregistré');
      setIsNewContratDialogOpen(false);
      fetchContratHistory(dossierEmployee.id);
      fetchEmployees();
      try {
        const refreshed = await axios.get(`${API_URL}/employees/${dossierEmployee.id}`);
        setDossierEmployee(refreshed.data);
      } catch (refreshError) {
        console.error('Error refreshing employee:', refreshError);
      }
    } catch (error) {
      console.error('Error creating contrat:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du contrat');
    }
  };

  // Sortie des effectifs
  const openSortieDialog = (employee) => {
    setSortieEmployee(employee);
    setSortieData({
      date_sortie: employee.date_fin_contrat_effective || employee.date_sortie || '',
      motif_fin_contrat: employee.motif_fin_contrat || (employee.type_contrat === 'CDD' ? 'fin_cdd' : ''),
      commentaire: ''
    });
    setIsSortieDialogOpen(true);
  };

  const handleSortie = async (e) => {
    e.preventDefault();
    if (!sortieEmployee) return;
    if (!sortieData.date_sortie || !sortieData.motif_fin_contrat) {
      toast.error('Date de sortie et motif obligatoires');
      return;
    }
    setSubmittingSortie(true);
    try {
      const response = await axios.post(`${API_URL}/employees/${sortieEmployee.id}/sortie`, {
        date_sortie: sortieData.date_sortie,
        motif_fin_contrat: sortieData.motif_fin_contrat,
        commentaire: sortieData.commentaire || null
      });
      toast.success(response.data?.message || 'Sortie des effectifs enregistrée');
      setIsSortieDialogOpen(false);
      setSortieEmployee(null);
      fetchEmployees();
      if (dossierEmployee && dossierEmployee.id === sortieEmployee.id) {
        try {
          const refreshed = await axios.get(`${API_URL}/employees/${sortieEmployee.id}`);
          setDossierEmployee(refreshed.data);
          fetchContratHistory(sortieEmployee.id);
        } catch (refreshError) {
          console.error('Error refreshing employee:', refreshError);
        }
      }
    } catch (error) {
      console.error('Error registering departure:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la sortie des effectifs';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setSubmittingSortie(false);
    }
  };

  const handleOpenDossier = async (employee) => {
    setDossierEmployee(employee);
    setIsDossierOpen(true);
    try {
      const response = await axios.get(`${API_URL}/employees/${employee.id}/dossier`);
      setDossierData(response.data);
    } catch (error) {
      console.error('Error fetching dossier:', error);
      toast.error('Erreur lors du chargement du dossier');
    }
    fetchContratHistory(employee.id);
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !dossierEmployee) return;

    const formDataObj = new FormData();
    formDataObj.append('file', uploadData.file);
    formDataObj.append('categorie', uploadData.categorie);
    formDataObj.append('titre', uploadData.titre || uploadData.file.name);
    formDataObj.append('description', uploadData.description);
    formDataObj.append('date_document', uploadData.date_document);

    try {
      await axios.post(`${API_URL}/employees/${dossierEmployee.id}/documents`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document ajouté au dossier');
      setIsUploadDialogOpen(false);
      setUploadData({ categorie: 'autre', titre: '', description: '', date_document: '', file: null });
      
      // Refresh dossier
      const response = await axios.get(`${API_URL}/employees/${dossierEmployee.id}/dossier`);
      setDossierData(response.data);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors de l\'upload');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/employees/${dossierEmployee.id}/documents/${docId}`);
      toast.success('Document supprimé');
      const response = await axios.get(`${API_URL}/employees/${dossierEmployee.id}/dossier`);
      setDossierData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/employees/${dossierEmployee.id}/documents/${doc.id}/download`);
      const docData = response.data;
      if (docData.data) {
        const byteCharacters = atob(docData.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: docData.content_type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);

        // If it's a viewable type (PDF or image), open in new tab
        const viewableTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
        if (viewableTypes.includes(docData.content_type)) {
          window.open(url, '_blank');
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.download = docData.filename || docData.titre || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } else {
        toast.error('Aucune donnée disponible pour ce document');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePreviewDocument = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/employees/${dossierEmployee.id}/documents/${doc.id}/download`);
      const docData = response.data;
      if (!docData.data) {
        toast.error('Aucune donnée disponible pour ce document');
        return;
      }
      const contentType = docData.content_type || 'application/octet-stream';
      const previewableTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];
      if (!previewableTypes.includes(contentType)) {
        // Type non prévisualisable : téléchargement classique
        handleDownloadDocument(doc);
        return;
      }
      setPreviewDoc({
        data: `data:${contentType};base64,${docData.data}`,
        filename: docData.filename || docData.titre || 'Document',
        sourceDoc: doc
      });
    } catch (error) {
      toast.error("Erreur lors du chargement de l'aperçu");
    }
  };

  const handleDownloadBulletin = async (bulletin) => {
    try {
      const response = await axios.get(`${API_URL}/bulletins-paie/${bulletin.id}/pdf`);
      const content = response.data.content;
      const filename = response.data.filename || `bulletin_paie_${bulletin.periode}.pdf`;
      if (!content) { toast.error('Bulletin non disponible'); return; }
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast.error('Erreur lors du téléchargement du bulletin');
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocument({
      id: doc.id,
      categorie: doc.categorie || 'autre',
      titre: doc.titre || '',
      description: doc.description || '',
      date_document: doc.date_document || '',
      newFile: null
    });
    setIsEditDocDialogOpen(true);
  };

  const handleSaveDocumentEdit = async () => {
    if (!editingDocument || !dossierEmployee) return;
    try {
      const formDataObj = new FormData();
      formDataObj.append('categorie', editingDocument.categorie);
      formDataObj.append('titre', editingDocument.titre);
      formDataObj.append('description', editingDocument.description);
      if (editingDocument.date_document) formDataObj.append('date_document', editingDocument.date_document);
      if (editingDocument.newFile) formDataObj.append('file', editingDocument.newFile);

      await axios.put(`${API_URL}/employees/${dossierEmployee.id}/documents/${editingDocument.id}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document mis à jour');
      setIsEditDocDialogOpen(false);
      setEditingDocument(null);
      const response = await axios.get(`${API_URL}/employees/${dossierEmployee.id}/dossier`);
      setDossierData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      sexe: '',
      date_naissance: '',
      lieu_naissance: '',
      pays_naissance: 'France',
      nationalite: 'Française',
      soumis_titre_sejour: false,
      numero_titre_sejour: '',
      date_expiration_titre_sejour: '',
      numero_secu: '',
      cle_secu: '',
      iban: '',
      bic: '',
      adresse: '',
      complement_adresse: '',
      code_postal: '',
      ville: '',
      situation_familiale: '',
      nb_enfants: 0,
      personne_urgence: '',
      tel_urgence: '',
      contact_urgence_affiliation: '',
      poste: '',
      departement: '',
      date_embauche: '',
      type_contrat: 'CDI',
      date_fin_contrat: '',
      sans_contrat: false,
      droit_paniers_repas: true,
      exclure_preparation_paie: false,
      motif_fin_contrat: '',
      date_fin_contrat_effective: '',
      matricule: '',
      matricule_paie: '',
      niveau: '',
      echelon: '',
      statut_professionnel: '',
      classification: '',
      coefficient: '',
      periode_essai_debut: '',
      periode_essai_fin: '',
      periode_essai_prolongation: '',
      periode_essai_duree_mois: '',
      convention_collective: '',
      duree_hebdo: 35,
      taux_horaire: '',
      salaire_base: '',
      statut: 'actif',
      creer_compte: false
    });
  };

  // Fonction pour copier dans le presse-papier
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      toast.error('Erreur lors de la copie');
    }
  };

  // Formater le numero de securite sociale pour la DPAE
  const formatNumeroSecu = (numero, cle) => {
    if (!numero) return 'Non renseigné';
    return `${numero}${cle ? ' ' + cle : ''}`;
  };

  // Ouvrir le site URSSAF DPAE
  const openUrssafDpae = () => {
    window.open('https://www.due.urssaf.fr/declarant/formulaireDueLibre.jsf', '_blank');
  };

  // Extraire les départements et types de contrat uniques pour les filtres
  const uniqueDepartments = [...new Set(employees.map(e => e.departement).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
  const uniqueContratTypes = [...new Set(employees.map(e => e.type_contrat).filter(Boolean))].sort();

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      emp.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.poste.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || emp.statut === filterStatus;
    const matchesDept = filterDepartment === 'all' || emp.departement === filterDepartment;
    const matchesContrat = filterContrat === 'all' || emp.type_contrat === filterContrat;

    return matchesSearch && matchesStatus && matchesDept && matchesContrat;
  }).sort((a, b) => {
    // Tri alphabétique (insensible à la casse et aux accents) sur la colonne choisie,
    // puis sur l'autre colonne nom/prénom pour départager
    const column = sortColumn || 'nom';
    const secondary = column === 'nom' ? 'prenom' : 'nom';
    const cmp = (a[column] || '').localeCompare(b[column] || '', 'fr', { sensitivity: 'base' });
    if (cmp !== 0) return sortDirection === 'desc' ? -cmp : cmp;
    return (a[secondary] || '').localeCompare(b[secondary] || '', 'fr', { sensitivity: 'base' });
  });

  const getCategoryIcon = (cat) => {
    const category = documentCategories.find(c => c.value === cat);
    if (!category) return FolderOpen;
    return category.icon;
  };

  const handleSort = (column) => {
    const nextDirection = sortColumn === column ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortColumn(column);
    setSortDirection(nextDirection);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ column, direction: nextDirection }));
    } catch {
      // localStorage indisponible : le tri n'est pas persisté
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-[#0F5156]" />
      : <ArrowDown className="w-3 h-3 ml-1 text-[#0F5156]" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Employés</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {employees.length} employé{employees.length > 1 ? 's' : ''} enregistré{employees.length > 1 ? 's' : ''}
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un employé
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedEmployee ? 'Modifier l\'employé' : 'Nouvel employé'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 py-4">
              <Tabs defaultValue="identite">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="identite">Identité</TabsTrigger>
                  <TabsTrigger value="contrat">Contrat</TabsTrigger>
                  <TabsTrigger value="remuneration">Rémunération</TabsTrigger>
                </TabsList>

                <TabsContent value="identite" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prénom *</Label>
                      <Input
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        placeholder="Jean"
                        required
                        data-testid="employee-prenom-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nom *</Label>
                      <Input
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        placeholder="Dupont"
                        required
                        data-testid="employee-nom-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="jean.dupont@entreprise.fr"
                        required
                        data-testid="employee-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone</Label>
                      <Input
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sexe</Label>
                      <Select
                        value={formData.sexe || ''}
                        onValueChange={(value) => setFormData({ ...formData, sexe: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="homme">Homme</SelectItem>
                          <SelectItem value="femme">Femme</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date de naissance *</Label>
                      <Input
                        type="date"
                        value={formData.date_naissance}
                        onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lieu de naissance</Label>
                      <Input
                        value={formData.lieu_naissance}
                        onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })}
                        placeholder="Paris"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Pays de naissance</Label>
                      <Select
                        value={formData.pays_naissance || 'France'}
                        onValueChange={(value) => setFormData({ ...formData, pays_naissance: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_COUNTRIES.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nationalité</Label>
                      <Select
                        value={formData.nationalite}
                        onValueChange={(value) => setFormData({ ...formData, nationalite: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_NATIONALITIES.map(nat => (
                            <SelectItem key={nat} value={nat}>{nat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Titre de séjour */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center space-x-2 mb-3">
                      <input
                        type="checkbox"
                        id="soumis_titre_sejour"
                        checked={formData.soumis_titre_sejour}
                        onChange={(e) => setFormData({ ...formData, soumis_titre_sejour: e.target.checked })}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <Label htmlFor="soumis_titre_sejour" className="text-sm font-medium text-blue-700 dark:text-blue-300 cursor-pointer">
                        Employé soumis à titre de séjour (hors Union Européenne)
                      </Label>
                    </div>
                    {formData.soumis_titre_sejour && (
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="space-y-2">
                          <Label className="text-xs">Numéro du titre de séjour</Label>
                          <Input
                            value={formData.numero_titre_sejour}
                            onChange={(e) => setFormData({ ...formData, numero_titre_sejour: e.target.value })}
                            placeholder="Ex: 123456789"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Date d'expiration</Label>
                          <Input
                            type="date"
                            value={formData.date_expiration_titre_sejour}
                            onChange={(e) => setFormData({ ...formData, date_expiration_titre_sejour: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Situation familiale</Label>
                      <Select
                        value={formData.situation_familiale}
                        onValueChange={(value) => setFormData({ ...formData, situation_familiale: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="celibataire">Célibataire</SelectItem>
                          <SelectItem value="marie">Marié(e)</SelectItem>
                          <SelectItem value="pacse">Pacsé(e)</SelectItem>
                          <SelectItem value="divorce">Divorcé(e)</SelectItem>
                          <SelectItem value="veuf">Veuf/Veuve</SelectItem>
                          <SelectItem value="concubinage">Concubinage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre d'enfants</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.nb_enfants}
                        onChange={(e) => setFormData({ ...formData, nb_enfants: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Contact d'urgence */}
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <Label className="text-sm font-medium mb-3 block text-red-700 dark:text-red-300">Contact d'urgence</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Nom de la personne</Label>
                        <Input
                          value={formData.personne_urgence}
                          onChange={(e) => setFormData({ ...formData, personne_urgence: e.target.value })}
                          placeholder="Nom du contact"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Téléphone d'urgence</Label>
                        <Input
                          value={formData.tel_urgence}
                          onChange={(e) => setFormData({ ...formData, tel_urgence: e.target.value })}
                          placeholder="06 00 00 00 00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Affiliation (lien)</Label>
                        <Select
                          value={formData.contact_urgence_affiliation || ''}
                          onValueChange={(value) => setFormData({ ...formData, contact_urgence_affiliation: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Lien avec le salarié" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="conjoint">Conjoint(e)</SelectItem>
                            <SelectItem value="pere">Père</SelectItem>
                            <SelectItem value="mere">Mère</SelectItem>
                            <SelectItem value="frere_soeur">Frère/Sœur</SelectItem>
                            <SelectItem value="enfant">Enfant</SelectItem>
                            <SelectItem value="ami">Ami(e)</SelectItem>
                            <SelectItem value="collegue">Collègue</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Numéro de sécurité sociale */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Label className="text-sm font-medium mb-3 block">Numéro de sécurité sociale</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Input
                          value={formData.numero_secu}
                          onChange={(e) => setFormData({ ...formData, numero_secu: e.target.value.replace(/\D/g, '').slice(0, 13) })}
                          placeholder="1 85 12 75 108 234"
                          maxLength={15}
                        />
                        <p className="text-xs text-slate-500">13 chiffres</p>
                      </div>
                      <div className="space-y-2">
                        <Input
                          value={formData.cle_secu}
                          onChange={(e) => setFormData({ ...formData, cle_secu: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                          placeholder="42"
                          maxLength={2}
                        />
                        <p className="text-xs text-slate-500">Clé (2 chiffres)</p>
                      </div>
                    </div>
                  </div>

                  {/* RIB / IBAN */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Label className="text-sm font-medium mb-3 block">Coordonnées bancaires (RIB)</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">IBAN</Label>
                        <Input
                          value={formData.iban}
                          onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                          placeholder="FR76 1234 5678 9012 3456 7890 123"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">BIC</Label>
                        <Input
                          value={formData.bic}
                          onChange={(e) => setFormData({ ...formData, bic: e.target.value.toUpperCase() })}
                          placeholder="BNPAFRPP"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Adresse</Label>
                    <Input
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                      placeholder="123 rue de la Paix"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Complément d'adresse</Label>
                    <Input
                      value={formData.complement_adresse}
                      onChange={(e) => setFormData({ ...formData, complement_adresse: e.target.value })}
                      placeholder="Bâtiment, étage, appartement..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Code postal</Label>
                      <Input
                        value={formData.code_postal}
                        onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                        placeholder="75001"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ville</Label>
                      <Input
                        value={formData.ville}
                        onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                        placeholder="Paris"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="contrat" className="space-y-4 mt-4">
                  {/* Option sans contrat pour la direction */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="sans_contrat"
                        checked={formData.sans_contrat}
                        onChange={(e) => setFormData({ ...formData, sans_contrat: e.target.checked })}
                        className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                      />
                      <Label htmlFor="sans_contrat" className="text-sm font-medium text-amber-700 dark:text-amber-300 cursor-pointer">
                        Pas de contrat (membre de la Direction)
                      </Label>
                    </div>
                    {formData.sans_contrat && (
                      <p className="text-xs text-amber-600 mt-2">Ce salarié sera intégré au SIRH sans contrat associé.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Poste *</Label>
                      <Input
                        value={formData.poste}
                        onChange={(e) => setFormData({ ...formData, poste: e.target.value })}
                        placeholder="Ex: Technicien, Commercial..."
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Département *</Label>
                      <Select
                        value={formData.departement}
                        onValueChange={(value) => setFormData({ ...formData, departement: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(departments).map(([code, dept]) => (
                            <SelectItem key={code} value={code}>{dept.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Matricule interne</Label>
                      <Input
                        value={formData.matricule}
                        onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
                        placeholder="Ex: EMP001"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Matricule Paie</Label>
                      <Input
                        value={formData.matricule_paie}
                        onChange={(e) => setFormData({ ...formData, matricule_paie: e.target.value })}
                        placeholder="Matricule logiciel de paie"
                      />
                      <p className="text-xs text-slate-500">Utilisé pour la synchronisation avec le logiciel de paie</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Date d'embauche *</Label>
                      <Input
                        type="date"
                        value={formData.date_embauche}
                        onChange={(e) => setFormData({ ...formData, date_embauche: e.target.value })}
                        required
                      />
                    </div>
                    {!formData.sans_contrat && (
                      <div className="space-y-2">
                        <Label>Type de contrat *</Label>
                        <Select
                          value={formData.type_contrat}
                          onValueChange={(value) => setFormData({ ...formData, type_contrat: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CDI">CDI</SelectItem>
                            <SelectItem value="CDD">CDD</SelectItem>
                            <SelectItem value="Interim">Intérim</SelectItem>
                            <SelectItem value="Prestataire">Prestataire externe</SelectItem>
                            <SelectItem value="Stage">Stage</SelectItem>
                            <SelectItem value="Apprentissage">Apprentissage</SelectItem>
                          </SelectContent>
                        </Select>
                        {TYPES_CONTRAT_HORS_PAIE.includes(formData.type_contrat) && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            Ce type de contrat est automatiquement exclu de la préparation de paie et le salaire n'est pas demandé.
                          </p>
                        )}
                      </div>
                    )}
                    {!formData.sans_contrat && formData.type_contrat && formData.type_contrat !== 'CDI' && (
                      <div className="space-y-2">
                        <Label>Date de fin de contrat</Label>
                        <Input
                          type="date"
                          value={formData.date_fin_contrat}
                          onChange={(e) => setFormData({ ...formData, date_fin_contrat: e.target.value })}
                        />
                      </div>
                    )}
                  </div>

                  {/* Options Paie */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <Label className="text-sm font-medium mb-3 block text-green-700 dark:text-green-300">Options Paie</Label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="droit_paniers_repas"
                          checked={formData.droit_paniers_repas}
                          onChange={(e) => setFormData({ ...formData, droit_paniers_repas: e.target.checked })}
                          className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                        />
                        <Label htmlFor="droit_paniers_repas" className="text-sm cursor-pointer">
                          Droit aux paniers repas
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="exclure_preparation_paie"
                          checked={formData.exclure_preparation_paie}
                          onChange={(e) => setFormData({ ...formData, exclure_preparation_paie: e.target.checked })}
                          className="h-4 w-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                        />
                        <Label htmlFor="exclure_preparation_paie" className="text-sm cursor-pointer text-red-700 dark:text-red-300">
                          Exclure de la préparation paie
                        </Label>
                      </div>
                      {formData.exclure_preparation_paie && (
                        <p className="text-xs text-red-500 ml-6">Ce salarié ne sera pas inclus dans la préparation de la paie mensuelle.</p>
                      )}
                    </div>
                  </div>

                  {/* Fin de contrat */}
                  {!formData.sans_contrat && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <Label className="text-sm font-medium mb-3 block">Fin de contrat</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Date effective de fin</Label>
                          <Input
                            type="date"
                            value={formData.date_fin_contrat_effective}
                            onChange={(e) => setFormData({ ...formData, date_fin_contrat_effective: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Motif de fin de contrat</Label>
                          <Select
                            value={formData.motif_fin_contrat || ''}
                            onValueChange={(value) => setFormData({ ...formData, motif_fin_contrat: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un motif" />
                            </SelectTrigger>
                            <SelectContent>
                              {MOTIFS_FIN_CONTRAT.map(m => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Nouveaux champs: Statut professionnel, Niveau, Échelon */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <Label className="text-sm font-medium mb-3 block">Classification</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Statut professionnel</Label>
                        <Select 
                          value={formData.statut_professionnel} 
                          onValueChange={(value) => setFormData({ ...formData, statut_professionnel: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cadre">Cadre</SelectItem>
                            <SelectItem value="agent_maitrise">Agent de maîtrise</SelectItem>
                            <SelectItem value="employe">Employé</SelectItem>
                            <SelectItem value="ouvrier">Ouvrier</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Niveau</Label>
                        <Input
                          value={formData.niveau}
                          onChange={(e) => setFormData({ ...formData, niveau: e.target.value })}
                          placeholder="Ex: III"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Échelon</Label>
                        <Input
                          value={formData.echelon}
                          onChange={(e) => setFormData({ ...formData, echelon: e.target.value })}
                          placeholder="Ex: 2"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Classification</Label>
                        <Input
                          value={formData.classification}
                          onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                          placeholder="Ex: E3"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Coefficient</Label>
                        <Input
                          value={formData.coefficient}
                          onChange={(e) => setFormData({ ...formData, coefficient: e.target.value })}
                          placeholder="Ex: 215"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Période d'essai */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                    <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Période d'essai
                    </Label>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Date de début</Label>
                        <Input
                          type="date"
                          value={formData.periode_essai_debut}
                          onChange={(e) => setFormData({ ...formData, periode_essai_debut: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Date de fin</Label>
                        <Input
                          type="date"
                          value={formData.periode_essai_fin}
                          onChange={(e) => setFormData({ ...formData, periode_essai_fin: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Durée (mois)</Label>
                        <Input
                          type="number"
                          value={formData.periode_essai_duree_mois}
                          onChange={(e) => setFormData({ ...formData, periode_essai_duree_mois: e.target.value })}
                          placeholder="2"
                          min="0"
                          max="12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Fin après prolongation</Label>
                        <Input
                          type="date"
                          value={formData.periode_essai_prolongation}
                          onChange={(e) => setFormData({ ...formData, periode_essai_prolongation: e.target.value })}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                      Une alerte sera générée 45 jours avant la fin de la période d'essai
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Convention collective</Label>
                      <Input
                        value={formData.convention_collective}
                        onChange={(e) => setFormData({ ...formData, convention_collective: e.target.value })}
                        placeholder="Ex: Convention collective applicable"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Statut</Label>
                      <Select 
                        value={formData.statut} 
                        onValueChange={(value) => setFormData({ ...formData, statut: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="actif">Actif</SelectItem>
                          <SelectItem value="inactif">Inactif</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="remuneration" className="space-y-4 mt-4">
                  {!canViewSalary ? (
                  <Card className="bg-slate-50 dark:bg-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        La rémunération n'est visible que par les utilisateurs disposant de la permission « Préparation paie » (RH, comptabilité, administrateur).
                      </p>
                    </CardContent>
                  </Card>
                  ) : TYPES_CONTRAT_HORS_PAIE.includes(formData.type_contrat) ? (
                  <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {formData.type_contrat === 'Interim' ? 'Intérimaire' : 'Prestataire externe'} : la rémunération est gérée par l'entreprise tierce.
                        Aucun salaire n'est demandé et ce salarié est automatiquement exclu de la préparation de paie.
                      </p>
                    </CardContent>
                  </Card>
                  ) : (
                  <>
                  <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Saisissez le taux horaire de base OU le salaire mensuel brut (majorations incluses si durée &gt; 35h). L'autre valeur sera calculée automatiquement.
                      </p>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Durée hebdo (h)
                      </Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.duree_hebdo}
                        onChange={(e) => handleDureeChange(e.target.value)}
                        placeholder="35"
                        data-testid="employee-duree-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-emerald-500" />
                        Taux horaire (€)
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.taux_horaire}
                        onChange={(e) => handleTauxChange(e.target.value)}
                        placeholder="12.50"
                        data-testid="employee-taux-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-[#0F5156]" />
                        Salaire mensuel (€)
                      </Label>
                      <Input
                        type="number"
                        step="1"
                        value={formData.salaire_base}
                        onChange={(e) => handleSalaireChange(e.target.value)}
                        placeholder="1895"
                        data-testid="employee-salaire-input"
                      />
                    </div>
                  </div>
                  
                  {formData.taux_horaire && formData.duree_hebdo && (() => {
                    const tauxH = parseFloat(formData.taux_horaire);
                    const heures = parseFloat(formData.duree_hebdo);
                    const maj = calculateMajorations(formData.taux_horaire, formData.duree_hebdo);

                    // Si <= 35h : calcul simple
                    if (!maj) {
                      return (
                        <Card className="bg-emerald-50 dark:bg-emerald-900/20">
                          <CardContent className="p-4">
                            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                              Calcul : {tauxH.toFixed(2)}€/h × {heures}h × {WEEKS_PER_MONTH_LABEL} sem = <span className="text-lg font-bold">{formatCurrency(formData.salaire_base)}</span> /mois
                            </p>
                          </CardContent>
                        </Card>
                      );
                    }

                    // Si > 35h : décomposition salaire avec majorations incluses
                    return (
                      <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
                        <CardContent className="p-4">
                          <h4 className="font-medium text-emerald-800 dark:text-emerald-300 mb-3 flex items-center gap-2">
                            <Euro className="w-4 h-4" />
                            Décomposition du salaire mensuel (majorations incluses)
                          </h4>
                          <div className="space-y-2 text-sm">
                            {/* Salaire base 35h */}
                            <div className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 rounded">
                              <div className="flex flex-col">
                                <span className="font-medium">Salaire brut base (0h → 35h)</span>
                                <span className="text-xs text-slate-500">35h × {tauxH.toFixed(2)}€/h × {WEEKS_PER_MONTH_LABEL} sem</span>
                              </div>
                              <span className="font-medium text-slate-700 dark:text-slate-300">{formatCurrency(maj.salaireBase35h)}</span>
                            </div>

                            {/* Paliers de majoration */}
                            {maj.tiers.map((tier, idx) => (
                              <div key={idx} className="flex justify-between items-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                                <div className="flex flex-col">
                                  <span className="font-medium text-amber-800 dark:text-amber-300">Majoration {tier.label} (+{tier.ratePercent}%)</span>
                                  <span className="text-xs text-slate-500">{tier.hours.toFixed(1)}h × {tier.effectiveRate.toFixed(2)}€/h majoré × {WEEKS_PER_MONTH_LABEL} sem</span>
                                </div>
                                <span className="font-bold text-amber-600 dark:text-amber-400">{formatCurrency(tier.monthlyAmount)}</span>
                              </div>
                            ))}

                            {/* Total */}
                            <div className="flex justify-between items-center p-3 bg-[#0F5156]/10 dark:bg-[#0F5156]/30 rounded-lg mt-1">
                              <div className="flex flex-col">
                                <span className="font-bold text-[#0F5156] dark:text-emerald-300">Total mensuel</span>
                                <span className="text-xs text-slate-500">
                                  dont {formatCurrency(maj.totalMajorations)} de majorations
                                </span>
                              </div>
                              <span className="text-xl font-bold text-[#0F5156] dark:text-emerald-300">{formatCurrency(formData.salaire_base)}</span>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            * Taux configurés dans Administration &gt; Convention & Paie ({structureConfig.taux_majoration_heures_sup_35_39}% de 35h à 39h, {structureConfig.taux_majoration_heures_sup_25}% de 39h à 43h, {structureConfig.taux_majoration_heures_sup_50}% au-delà de 43h)
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })()}
                  </>
                  )}
                </TabsContent>
              </Tabs>

              {!selectedEmployee && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="creer_compte"
                      checked={formData.creer_compte}
                      onChange={(e) => setFormData({ ...formData, creer_compte: e.target.checked })}
                      className="h-4 w-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                    />
                    <Label htmlFor="creer_compte" className="text-sm font-medium text-emerald-700 dark:text-emerald-300 cursor-pointer flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Créer un compte utilisateur pour cet employé
                    </Label>
                  </div>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 ml-6">
                    Un email de confirmation sera envoyé à l'adresse email de l'employé pour qu'il puisse accéder au portail employé.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="employee-submit-btn">
                  {selectedEmployee ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email, poste..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="employee-search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-32">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="actif">Actifs</SelectItem>
                <SelectItem value="inactif">Inactifs</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-full sm:w-40">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les départements</SelectItem>
                {uniqueDepartments.map(dept => (
                  <SelectItem key={dept} value={dept}>{departments[dept]?.label || dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterContrat} onValueChange={setFilterContrat}>
              <SelectTrigger className="w-full sm:w-36">
                <Briefcase className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Contrat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les contrats</SelectItem>
                {uniqueContratTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header cursor-pointer select-none" onClick={() => handleSort('nom')}>
                    <div className="flex items-center">Nom <SortIcon column="nom" /></div>
                  </TableHead>
                  <TableHead className="table-header cursor-pointer select-none" onClick={() => handleSort('prenom')}>
                    <div className="flex items-center">Prenom <SortIcon column="prenom" /></div>
                  </TableHead>
                  <TableHead className="table-header hidden md:table-cell">Poste</TableHead>
                  <TableHead className="table-header hidden md:table-cell">Matricule</TableHead>
                  <TableHead className="table-header">Statut</TableHead>
                  <TableHead className="table-header text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500" data-testid="employees-empty">
                      Aucun employé trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => (
                    <TableRow key={employee.id} className="table-row" data-testid={`employee-row-${employee.id}`}>
                      <TableCell className="table-cell">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-[#0F5156] text-white text-sm">
                              {employee.prenom?.[0]}{employee.nom?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{employee.nom}</p>
                              {employee.alerte_titre_sejour && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                    employee.alerte_titre_sejour.niveau === 'critique'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-amber-100 text-amber-800'
                                  }`}
                                  title={employee.alerte_titre_sejour.message}
                                >
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Titre séjour
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{employee.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="table-cell">
                        <p className="font-medium">{employee.prenom}</p>
                      </TableCell>
                      <TableCell className="table-cell hidden md:table-cell">
                        <div>
                          <p>{employee.poste}</p>
                          <p className="text-xs text-slate-500">{departments[employee.departement]?.label || employee.departement}</p>
                        </div>
                      </TableCell>
                      <TableCell className="table-cell hidden md:table-cell">
                        <div className="space-y-1">
                          {employee.matricule && (
                            <Badge variant="outline" className="text-xs">{employee.matricule}</Badge>
                          )}
                          {employee.matricule_paie && (
                            <Badge variant="secondary" className="text-xs block">{employee.matricule_paie}</Badge>
                          )}
                          {!employee.matricule && !employee.matricule_paie && (
                            <span className="text-slate-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="flex flex-col items-start gap-1">
                          <Badge className={getStatusColor(employee.statut)}>
                            {getStatusLabel(employee.statut)}
                          </Badge>
                          {employee.statut !== 'actif' && employee.date_sortie && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                              title={employee.motif_fin_contrat ? `Motif : ${getMotifFinLabel(employee.motif_fin_contrat)}` : undefined}
                            >
                              <LogOut className="w-3 h-3 mr-1" />
                              Sorti le {formatDateShort(employee.date_sortie)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDossier(employee)}
                            data-testid={`employee-dossier-${employee.id}`}
                          >
                            <FolderOpen className="w-4 h-4 text-[#0F5156]" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(employee)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenDossier(employee)}>
                                <FolderOpen className="w-4 h-4 mr-2" />
                                Dossier salarié
                              </DropdownMenuItem>
                              {canEditEmployees && employee.statut === 'actif' && (
                                <DropdownMenuItem onClick={() => openSortieDialog(employee)} className="text-amber-700">
                                  <LogOut className="w-4 h-4 mr-2" />
                                  Sortie des effectifs
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDelete(employee.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dossier Salarié Sheet */}
      <Sheet open={isDossierOpen} onOpenChange={setIsDossierOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              <FolderOpen className="w-6 h-6 text-[#0F5156]" />
              Dossier Salarié
            </SheetTitle>
          </SheetHeader>
          
          {dossierEmployee && (
            <div className="mt-6 space-y-6">
              {/* Employee Info Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <Avatar className="w-14 h-14">
                  <AvatarFallback className="bg-[#0F5156] text-white text-lg">
                    {dossierEmployee.prenom[0]}{dossierEmployee.nom[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-lg">{dossierEmployee.prenom} {dossierEmployee.nom}</h3>
                    <Badge className={getStatusColor(dossierEmployee.statut)}>{getStatusLabel(dossierEmployee.statut)}</Badge>
                    {dossierEmployee.statut !== 'actif' && dossierEmployee.date_sortie && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                        <LogOut className="w-3 h-3 mr-1" />
                        Sorti le {formatDateShort(dossierEmployee.date_sortie)}
                        {dossierEmployee.motif_fin_contrat ? ` · ${getMotifFinLabel(dossierEmployee.motif_fin_contrat)}` : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{dossierEmployee.poste}</p>
                  <p className="text-xs text-slate-400">Embauché le {formatDateShort(dossierEmployee.date_embauche)}</p>
                  {canViewSalary && !TYPES_CONTRAT_HORS_PAIE.includes(dossierEmployee.type_contrat) && (
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <Euro className="w-3 h-3" />
                      {dossierEmployee.salaire_base ? `${formatCurrency(dossierEmployee.salaire_base)} brut/mois` : 'Salaire non renseigné'}
                      {dossierEmployee.taux_horaire ? ` · ${parseFloat(dossierEmployee.taux_horaire).toFixed(2)} €/h` : ''}
                      {dossierEmployee.duree_hebdo ? ` · ${dossierEmployee.duree_hebdo}h/sem` : ''}
                    </p>
                  )}
                </div>
                {canEditEmployees && dossierEmployee.statut === 'actif' && (
                  <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => openSortieDialog(dossierEmployee)}>
                    <LogOut className="w-4 h-4 mr-1" />
                    Sortie des effectifs
                  </Button>
                )}
              </div>

              {/* Contrats & Avenants */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-500" />
                      Contrats & Avenants
                    </CardTitle>
                    <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-[#0F5156] hover:bg-[#0d464a]" onClick={() => {
                      setAvenantData({
                        type_contrat: dossierEmployee.type_contrat || 'CDI',
                        date_debut: '',
                        date_fin: '',
                        poste: dossierEmployee.poste || '',
                        departement: dossierEmployee.departement || '',
                        duree_hebdo: dossierEmployee.duree_hebdo || 35,
                        salaire_base: dossierEmployee.salaire_base || '',
                        taux_horaire: dossierEmployee.taux_horaire || '',
                        motif_avenant: '',
                        niveau: dossierEmployee.niveau || '',
                        echelon: dossierEmployee.echelon || '',
                        statut_professionnel: dossierEmployee.statut_professionnel || ''
                      });
                      setIsAvenantDialogOpen(true);
                    }}>
                      <Plus className="w-4 h-4 mr-1" />
                      Nouvel avenant
                    </Button>
                    <Button size="sm" variant="outline" className="border-[#0F5156] text-[#0F5156]" onClick={openNewContratDialog}>
                      <FilePlus2 className="w-4 h-4 mr-1" />
                      Nouveau contrat
                    </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {contratHistory.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Aucun contrat enregistré</p>
                  ) : (
                    <div className="space-y-3">
                      {contratHistory.map((contrat, idx) => (
                        <div key={contrat.id} className={`p-3 rounded-lg border ${contrat.statut === 'actif' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 bg-slate-50 dark:bg-slate-800'}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant={contrat.est_avenant ? 'secondary' : 'default'}
                                       className={contrat.est_avenant ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'}>
                                  {contrat.est_avenant ? 'Avenant' : (contrat.motif_creation ? 'Nouveau contrat' : 'Contrat initial')}
                                </Badge>
                                <Badge variant="outline" className={contrat.statut === 'actif' ? 'border-emerald-500 text-emerald-700' : 'border-slate-400 text-slate-600'}>
                                  {contrat.statut === 'actif' ? 'Actif' : contrat.statut === 'termine' ? 'Terminé' : contrat.statut}
                                </Badge>
                              </div>
                              <p className="text-sm font-medium mt-1">{contrat.poste} - {contrat.type_contrat}</p>
                              {contrat.est_avenant && contrat.motif_avenant && (
                                <p className="text-xs text-indigo-600 mt-0.5">Motif : {contrat.motif_avenant}</p>
                              )}
                              {!contrat.est_avenant && contrat.motif_creation && (
                                <p className="text-xs text-blue-600 mt-0.5">Motif : {contrat.motif_creation}</p>
                              )}
                              <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                <p>Début : {new Date(contrat.date_debut + 'T00:00:00').toLocaleDateString('fr-FR')}</p>
                                {contrat.date_fin && <p>Fin prévue : {new Date(contrat.date_fin + 'T00:00:00').toLocaleDateString('fr-FR')}</p>}
                                {contrat.date_fin_effective && <p>Fin effective : {new Date(contrat.date_fin_effective + 'T00:00:00').toLocaleDateString('fr-FR')}</p>}
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              {canViewSalary && contrat.salaire_base && <p className="font-medium">{parseFloat(contrat.salaire_base).toLocaleString('fr-FR')} &euro;</p>}
                              <p className="text-xs text-slate-500">{contrat.duree_hebdo}h/sem</p>
                              {contrat.departement && <p className="text-xs text-slate-400">{departments[contrat.departement]?.label || contrat.departement}</p>}
                            </div>
                          </div>
                          {contrat.motif_fin && (
                            <p className="text-xs text-red-600 mt-1">Fin : {contrat.motif_fin}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add Document Button */}
              <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-[#0F5156] hover:bg-[#0d464a]" data-testid="add-document-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Ajouter un document
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter un document au dossier</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUploadDocument} className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Catégorie *</Label>
                      <Select 
                        value={uploadData.categorie} 
                        onValueChange={(value) => setUploadData({ ...uploadData, categorie: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {documentCategories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              <div className="flex items-center gap-2">
                                <cat.icon className={`w-4 h-4 ${cat.color}`} />
                                {cat.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Titre</Label>
                      <Input
                        value={uploadData.titre}
                        onChange={(e) => setUploadData({ ...uploadData, titre: e.target.value })}
                        placeholder="Ex: CDI - Contrat initial"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date du document</Label>
                      <Input
                        type="date"
                        value={uploadData.date_document}
                        onChange={(e) => setUploadData({ ...uploadData, date_document: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={uploadData.description}
                        onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                        placeholder="Notes ou commentaires..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fichier *</Label>
                      <Input
                        type="file"
                        onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                        Ajouter
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Documents by Category */}
              {dossierData && (() => {
                const cats = dossierData.categories || {};
                const catEntries = Object.entries(cats);
                return (
                <Tabs defaultValue={catEntries[0]?.[0] || 'arrets'} className="w-full">
                  <div className="flex flex-col sm:flex-row gap-4">
                  <TabsList className="flex sm:flex-col h-auto w-full sm:w-52 shrink-0 items-stretch gap-1 p-1 flex-wrap sm:flex-nowrap">
                    {catEntries.map(([code, info]) => (
                      <TabsTrigger key={code} value={code} className="text-xs w-full justify-between px-3 py-2">
                        <span className="truncate text-left">{info.label}</span>
                        <span className="ml-2 shrink-0 text-slate-500">{dossierData.stats?.[code] || 0}</span>
                      </TabsTrigger>
                    ))}
                    <TabsTrigger value="arrets" className="text-xs w-full justify-between px-3 py-2">
                      <span className="truncate text-left">Arrêts</span>
                      <span className="ml-2 shrink-0 text-slate-500">{dossierData.stats?.arrets_maladie || 0}</span>
                    </TabsTrigger>
                    <TabsTrigger value="bulletins_paie" className="text-xs w-full justify-between px-3 py-2">
                      <span className="truncate text-left">Bulletins paie</span>
                      <span className="ml-2 shrink-0 text-slate-500">{dossierData.stats?.bulletins_paie || 0}</span>
                    </TabsTrigger>
                    {(dossierData.non_classes || []).length > 0 && (
                      <TabsTrigger value="non_classes" className="text-xs w-full justify-between px-3 py-2">
                        <span className="truncate text-left">Non classés</span>
                        <span className="ml-2 shrink-0 text-slate-500">{dossierData.non_classes.length}</span>
                      </TabsTrigger>
                    )}
                  </TabsList>

                  <div className="flex-1 min-w-0">
                  {catEntries.map(([code, info]) => (
                    <TabsContent key={code} value={code} className="mt-4 sm:mt-0">
                      <DocumentList
                        documents={dossierData[code] || []}
                        onDelete={handleDeleteDocument}
                        onEdit={handleEditDocument}
                        onDownload={handleDownloadDocument}
                        onPreview={handlePreviewDocument}
                        emptyMessage={`Aucun document ${info.label.toLowerCase()}`}
                      />
                    </TabsContent>
                  ))}

                  <TabsContent value="arrets" className="mt-4">
                    {dossierData.arrets_maladie?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Aucun arrêt maladie</p>
                    ) : (
                      <div className="space-y-3">
                        {dossierData.arrets_maladie?.map((arret, i) => (
                          <Card key={i} className="bg-red-50 dark:bg-red-900/20">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium">Arrêt maladie</p>
                                  <p className="text-sm text-slate-500">
                                    Du {formatDateShort(arret.date_debut)} au {formatDateShort(arret.date_fin)}
                                  </p>
                                  <p className="text-sm">{arret.nb_jours} jour(s)</p>
                                </div>
                                {arret.justificatif_url ? (
                                  <Badge className="bg-emerald-100 text-emerald-800">Justificatif joint</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-800">Sans justificatif</Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="bulletins_paie" className="mt-4">
                    {(dossierData.bulletins_paie || []).length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Aucun bulletin de paie importé</p>
                    ) : (
                      <div className="space-y-2">
                        {dossierData.bulletins_paie.map((bulletin) => (
                          <div key={bulletin.id} className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-emerald-100 dark:bg-emerald-800 rounded">
                                <FileText className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">Bulletin de paie - {bulletin.periode}</p>
                                <div className="flex gap-3 text-xs text-slate-500">
                                  {bulletin.montant_net ? <span>Net: {Number(bulletin.montant_net).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span> : null}
                                  {bulletin.matricule_paie && <span>Mat: {bulletin.matricule_paie}</span>}
                                  <span>Source: {bulletin.source === 'upload' ? 'Dépôt' : (bulletin.source || 'SILAE')}</span>
                                  {bulletin.imported_at && <span>Importé le {new Date(bulletin.imported_at).toLocaleDateString('fr-FR')}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {bulletin.statut === 'distribue' ? (
                                <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" title={bulletin.distribue_par_email ? `Distribué par ${bulletin.distribue_par_email}` : 'Distribué'}>
                                  Distribué{bulletin.distribue_le ? ` le ${new Date(bulletin.distribue_le).toLocaleDateString('fr-FR')}` : ''}
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800 text-[10px]" title="Non visible par le salarié tant qu'il n'est pas distribué depuis Préparation paie › Bulletins">
                                  À vérifier / non distribué
                                </Badge>
                              )}
                            <Button variant="outline" size="sm" onClick={() => handleDownloadBulletin(bulletin)}>
                              <Download className="w-4 h-4" />
                            </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {(dossierData.non_classes || []).length > 0 && (
                    <TabsContent value="non_classes" className="mt-0 sm:mt-0">
                      <DocumentList
                        documents={dossierData.non_classes || []}
                        onDelete={handleDeleteDocument}
                        onDownload={handleDownloadDocument}
                        onPreview={handlePreviewDocument}
                        emptyMessage=""
                      />
                    </TabsContent>
                  )}
                  </div>
                  </div>
                </Tabs>
                );
              })()}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Modal DPAE - Declaration Prealable a l'Embauche */}
      <Dialog open={isDpaeDialogOpen} onOpenChange={setIsDpaeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-[#0F5156]" />
              Declaration Prealable a l'Embauche (DPAE)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Message d'information */}
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">Obligation legale</p>
                    <p>La DPAE doit etre effectuee au plus tot 8 jours avant l'embauche et au plus tard le jour de l'embauche, avant la prise de fonction effective du salarie.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {newlyCreatedEmployee && (
              <>
                {/* Informations de l'employe */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Informations du salarie
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <DpaeInfoField
                      label="Nom"
                      value={newlyCreatedEmployee.nom}
                      onCopy={() => copyToClipboard(newlyCreatedEmployee.nom, 'nom')}
                      copied={copiedField === 'nom'}
                    />
                    <DpaeInfoField
                      label="Prenom"
                      value={newlyCreatedEmployee.prenom}
                      onCopy={() => copyToClipboard(newlyCreatedEmployee.prenom, 'prenom')}
                      copied={copiedField === 'prenom'}
                    />
                    <DpaeInfoField
                      label="Date de naissance"
                      value={formatDateShort(newlyCreatedEmployee.date_naissance)}
                      onCopy={() => copyToClipboard(newlyCreatedEmployee.date_naissance, 'date_naissance')}
                      copied={copiedField === 'date_naissance'}
                    />
                    <DpaeInfoField
                      label="Lieu de naissance"
                      value={newlyCreatedEmployee.lieu_naissance || 'Non renseigne'}
                      onCopy={() => copyToClipboard(newlyCreatedEmployee.lieu_naissance || '', 'lieu_naissance')}
                      copied={copiedField === 'lieu_naissance'}
                    />
                    <DpaeInfoField
                      label="N° Securite sociale"
                      value={formatNumeroSecu(newlyCreatedEmployee.numero_secu, newlyCreatedEmployee.cle_secu)}
                      onCopy={() => copyToClipboard(`${newlyCreatedEmployee.numero_secu || ''}${newlyCreatedEmployee.cle_secu || ''}`, 'numero_secu')}
                      copied={copiedField === 'numero_secu'}
                      className="col-span-2"
                    />
                  </div>
                </div>

                {/* Informations du contrat */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Informations du contrat
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <DpaeInfoField
                      label="Date d'embauche"
                      value={formatDateShort(newlyCreatedEmployee.date_embauche)}
                      onCopy={() => copyToClipboard(newlyCreatedEmployee.date_embauche, 'date_embauche')}
                      copied={copiedField === 'date_embauche'}
                    />
                    <DpaeInfoField
                      label="Type de contrat"
                      value={newlyCreatedEmployee.type_contrat}
                      onCopy={() => copyToClipboard(newlyCreatedEmployee.type_contrat, 'type_contrat')}
                      copied={copiedField === 'type_contrat'}
                    />
                    <DpaeInfoField
                      label="Duree hebdomadaire"
                      value={`${newlyCreatedEmployee.duree_hebdo || 35}h`}
                      onCopy={() => copyToClipboard(String(newlyCreatedEmployee.duree_hebdo || 35), 'duree_hebdo')}
                      copied={copiedField === 'duree_hebdo'}
                    />
                    {newlyCreatedEmployee.type_contrat !== 'CDI' && newlyCreatedEmployee.date_fin_contrat && (
                      <DpaeInfoField
                        label="Date de fin prevue"
                        value={formatDateShort(newlyCreatedEmployee.date_fin_contrat)}
                        onCopy={() => copyToClipboard(newlyCreatedEmployee.date_fin_contrat, 'date_fin_contrat')}
                        copied={copiedField === 'date_fin_contrat'}
                      />
                    )}
                  </div>
                </div>

                {/* Informations de l'employeur */}
                {structureInfo && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Informations de l'employeur
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <DpaeInfoField
                        label="Raison sociale"
                        value={structureInfo.nom}
                        onCopy={() => copyToClipboard(structureInfo.nom, 'structure_nom')}
                        copied={copiedField === 'structure_nom'}
                      />
                      <DpaeInfoField
                        label="SIRET"
                        value={structureInfo.siret || 'Non renseigne'}
                        onCopy={() => copyToClipboard(structureInfo.siret || '', 'siret')}
                        copied={copiedField === 'siret'}
                      />
                      {structureInfo.adresse && (
                        <DpaeInfoField
                          label="Adresse"
                          value={structureInfo.adresse}
                          onCopy={() => copyToClipboard(structureInfo.adresse, 'structure_adresse')}
                          copied={copiedField === 'structure_adresse'}
                          className="col-span-2"
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Heure d'embauche pour soumission automatique */}
            {urssafConfig?.configured && (
              <div className="space-y-2">
                <Label>Heure d'embauche prevue</Label>
                <Input
                  type="time"
                  value={heureEmbauche}
                  onChange={(e) => setHeureEmbauche(e.target.value)}
                  className="w-32"
                />
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex flex-col gap-3 pt-4 border-t">
              {/* Soumission automatique si configure */}
              {urssafConfig?.configured ? (
                <>
                  <Button
                    onClick={submitDpaeToUrssaf}
                    disabled={submittingDpae}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
                  >
                    {submittingDpae ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                        Soumission en cours...
                      </>
                    ) : (
                      <>
                        <ClipboardCheck className="w-5 h-5 mr-2" />
                        Soumettre la DPAE automatiquement
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-emerald-600">
                    La DPAE sera soumise directement a l'URSSAF via l'API{urssafConfig.urssaf_use_test ? ' (mode test)' : ''}.
                  </p>
                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-slate-200"></div>
                    <span className="flex-shrink mx-4 text-slate-400 text-sm">ou</span>
                    <div className="flex-grow border-t border-slate-200"></div>
                  </div>
                </>
              ) : (
                <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
                  <CardContent className="p-3">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Configurez vos identifiants URSSAF dans Administration &gt; URSSAF DPAE pour activer la soumission automatique.
                    </p>
                  </CardContent>
                </Card>
              )}

              <Button
                onClick={openUrssafDpae}
                variant={urssafConfig?.configured ? "outline" : "default"}
                className={urssafConfig?.configured ? "w-full h-10" : "w-full bg-[#0F5156] hover:bg-[#0d464a] h-12 text-base"}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                {urssafConfig?.configured ? 'Ouvrir le site URSSAF' : 'Effectuer la DPAE sur urssaf.fr'}
              </Button>
              {!urssafConfig?.configured && (
                <p className="text-xs text-center text-slate-500">
                  Vous serez redirige vers le formulaire officiel de l'URSSAF.
                  Utilisez les boutons de copie ci-dessus pour transferer facilement les informations.
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsDpaeDialogOpen(false)}
                  className="flex-1"
                >
                  Faire plus tard
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsDpaeDialogOpen(false);
                    toast.success('N\'oubliez pas d\'effectuer la DPAE avant l\'embauche');
                  }}
                  className="flex-1"
                >
                  DPAE deja effectuee
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog modification document */}
      <EditDocumentDialog
        isOpen={isEditDocDialogOpen}
        onClose={setIsEditDocDialogOpen}
        doc={editingDocument}
        documentCategories={documentCategories}
        onSave={(updatedDoc, doSave) => {
          if (doSave) {
            setEditingDocument(updatedDoc);
            handleSaveDocumentEdit();
          } else {
            setEditingDocument(updatedDoc);
          }
        }}
      />

      {/* Dialog création avenant */}
      <Dialog open={isAvenantDialogOpen} onOpenChange={setIsAvenantDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              Nouvel avenant {dossierEmployee ? `- ${dossierEmployee.prenom} ${dossierEmployee.nom}` : ''}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAvenant} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motif de l'avenant *</Label>
              <Select value={avenantData.motif_avenant} onValueChange={(v) => setAvenantData({...avenantData, motif_avenant: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le motif" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Augmentation de salaire">Augmentation de salaire</SelectItem>
                  <SelectItem value="Réduction d'heures">Réduction d'heures</SelectItem>
                  <SelectItem value="Augmentation d'heures">Augmentation d'heures</SelectItem>
                  <SelectItem value="Changement de poste">Changement de poste</SelectItem>
                  <SelectItem value="Changement de département">Changement de département</SelectItem>
                  <SelectItem value="Changement de conditions">Changement de conditions</SelectItem>
                  <SelectItem value="Promotion">Promotion</SelectItem>
                  <SelectItem value="Passage temps partiel">Passage temps partiel</SelectItem>
                  <SelectItem value="Passage temps plein">Passage temps plein</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début de l'avenant *</Label>
                <Input type="date" value={avenantData.date_debut} onChange={(e) => setAvenantData({...avenantData, date_debut: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Date de fin (si applicable)</Label>
                <Input type="date" value={avenantData.date_fin} onChange={(e) => setAvenantData({...avenantData, date_fin: e.target.value})} />
              </div>
            </div>
            <Separator />
            <p className="text-sm text-slate-500">Modifiez uniquement les champs qui changent :</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de contrat</Label>
                <Select value={avenantData.type_contrat} onValueChange={(v) => setAvenantData({...avenantData, type_contrat: v})}>
                  <SelectTrigger><SelectValue placeholder="Inchangé" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Interim">Intérim</SelectItem>
                    <SelectItem value="Prestataire">Prestataire externe</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                    <SelectItem value="Apprentissage">Apprentissage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Poste</Label>
                <Input value={avenantData.poste} onChange={(e) => setAvenantData({...avenantData, poste: e.target.value})} placeholder={dossierEmployee?.poste || ''} />
              </div>
              <div className="space-y-2">
                <Label>Département</Label>
                <Select value={avenantData.departement} onValueChange={(v) => setAvenantData({...avenantData, departement: v})}>
                  <SelectTrigger><SelectValue placeholder={departments[dossierEmployee?.departement]?.label || 'Inchangé'} /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(departments).map(([code, dept]) => (
                      <SelectItem key={code} value={code}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Durée hebdomadaire (h)</Label>
                <Input type="number" step="0.5" value={avenantData.duree_hebdo} onChange={(e) => setAvenantData(applySalaryField(avenantData, 'duree_hebdo', e.target.value))} />
              </div>
              {canViewSalary && !TYPES_CONTRAT_HORS_PAIE.includes(avenantData.type_contrat) && (
                <>
                  <div className="space-y-2">
                    <Label>Salaire brut mensuel (&euro;)</Label>
                    <Input type="number" step="0.01" value={avenantData.salaire_base} onChange={(e) => setAvenantData(applySalaryField(avenantData, 'salaire_base', e.target.value))} placeholder={dossierEmployee?.salaire_base?.toString() || ''} />
                  </div>
                  <div className="space-y-2">
                    <Label>Taux horaire (&euro;)</Label>
                    <Input type="number" step="0.01" value={avenantData.taux_horaire} onChange={(e) => setAvenantData(applySalaryField(avenantData, 'taux_horaire', e.target.value))} placeholder={dossierEmployee?.taux_horaire?.toString() || ''} />
                    {avenantData.taux_horaire && avenantData.duree_hebdo && (
                      <p className="text-xs text-slate-500">
                        {parseFloat(avenantData.taux_horaire).toFixed(2)} €/h × {calculateEffectiveHours(avenantData.duree_hebdo).toFixed(2)}h × {WEEKS_PER_MONTH_LABEL} sem (majorations incluses)
                      </p>
                    )}
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>Niveau</Label>
                <Input value={avenantData.niveau} onChange={(e) => setAvenantData({...avenantData, niveau: e.target.value})} placeholder={dossierEmployee?.niveau || ''} />
              </div>
              <div className="space-y-2">
                <Label>Échelon</Label>
                <Input value={avenantData.echelon} onChange={(e) => setAvenantData({...avenantData, echelon: e.target.value})} placeholder={dossierEmployee?.echelon || ''} />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAvenantDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" disabled={!avenantData.date_debut || !avenantData.motif_avenant}>
                Créer l'avenant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog nouveau contrat (l'ancien contrat est clôturé et conservé dans l'historique) */}
      <Dialog open={isNewContratDialogOpen} onOpenChange={setIsNewContratDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="w-5 h-5 text-[#0F5156]" />
              Nouveau contrat {dossierEmployee ? `- ${dossierEmployee.prenom} ${dossierEmployee.nom}` : ''}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNewContrat} className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Le contrat actuel sera clôturé la veille de la date de début et conservé dans l'historique.
            </p>
            <div className="space-y-2">
              <Label>Motif *</Label>
              <Select value={newContratData.motif_creation} onValueChange={(v) => setNewContratData({ ...newContratData, motif_creation: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le motif" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIFS_NOUVEAU_CONTRAT.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type de contrat *</Label>
                <Select value={newContratData.type_contrat} onValueChange={(v) => setNewContratData({ ...newContratData, type_contrat: v, date_fin: v === 'CDI' ? '' : newContratData.date_fin })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Interim">Intérim</SelectItem>
                    <SelectItem value="Prestataire">Prestataire externe</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                    <SelectItem value="Apprentissage">Apprentissage</SelectItem>
                  </SelectContent>
                </Select>
                {TYPES_CONTRAT_HORS_PAIE.includes(newContratData.type_contrat) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">Type exclu de la préparation de paie : aucun salaire n'est demandé.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Poste</Label>
                <Input value={newContratData.poste} onChange={(e) => setNewContratData({ ...newContratData, poste: e.target.value })} placeholder={dossierEmployee?.poste || ''} />
              </div>
              <div className="space-y-2">
                <Label>Date de début *</Label>
                <Input type="date" value={newContratData.date_debut} onChange={(e) => setNewContratData({ ...newContratData, date_debut: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Date de fin {newContratData.type_contrat !== 'CDI' ? '*' : ''}</Label>
                <Input
                  type="date"
                  value={newContratData.date_fin}
                  onChange={(e) => setNewContratData({ ...newContratData, date_fin: e.target.value })}
                  disabled={newContratData.type_contrat === 'CDI'}
                  required={newContratData.type_contrat !== 'CDI'}
                  min={newContratData.date_debut || undefined}
                />
              </div>
              <div className="space-y-2">
                <Label>Département</Label>
                <Select value={newContratData.departement} onValueChange={(v) => setNewContratData({ ...newContratData, departement: v })}>
                  <SelectTrigger><SelectValue placeholder={departments[dossierEmployee?.departement]?.label || 'Inchangé'} /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(departments).map(([code, dept]) => (
                      <SelectItem key={code} value={code}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Durée hebdomadaire (h)</Label>
                <Input type="number" step="0.5" value={newContratData.duree_hebdo} onChange={(e) => setNewContratData(applySalaryField(newContratData, 'duree_hebdo', e.target.value))} />
              </div>
              {canViewSalary && !TYPES_CONTRAT_HORS_PAIE.includes(newContratData.type_contrat) && (
                <>
                  <div className="space-y-2">
                    <Label>Salaire brut mensuel (&euro;)</Label>
                    <Input type="number" step="0.01" value={newContratData.salaire_base} onChange={(e) => setNewContratData(applySalaryField(newContratData, 'salaire_base', e.target.value))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Taux horaire (&euro;)</Label>
                    <Input type="number" step="0.01" value={newContratData.taux_horaire} onChange={(e) => setNewContratData(applySalaryField(newContratData, 'taux_horaire', e.target.value))} />
                    {newContratData.taux_horaire && newContratData.duree_hebdo && (
                      <p className="text-xs text-slate-500">
                        {parseFloat(newContratData.taux_horaire).toFixed(2)} €/h × {calculateEffectiveHours(newContratData.duree_hebdo).toFixed(2)}h × {WEEKS_PER_MONTH_LABEL} sem (majorations incluses)
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsNewContratDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" disabled={!newContratData.date_debut || !newContratData.motif_creation || !newContratData.type_contrat}>
                Enregistrer le contrat
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog sortie des effectifs */}
      <Dialog open={isSortieDialogOpen} onOpenChange={(open) => { setIsSortieDialogOpen(open); if (!open) setSortieEmployee(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5 text-amber-600" />
              Sortie des effectifs {sortieEmployee ? `- ${sortieEmployee.prenom} ${sortieEmployee.nom}` : ''}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSortie} className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              L'employé passe en inactif à la date de sortie (immédiatement si elle est déjà atteinte, sinon automatiquement le jour venu). Son contrat actif est clôturé et son dossier reste consultable.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de sortie *</Label>
                <Input
                  type="date"
                  value={sortieData.date_sortie}
                  min={sortieEmployee?.date_embauche || undefined}
                  onChange={(e) => setSortieData({ ...sortieData, date_sortie: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Motif de fin de contrat *</Label>
                <Select value={sortieData.motif_fin_contrat} onValueChange={(v) => setSortieData({ ...sortieData, motif_fin_contrat: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un motif" />
                  </SelectTrigger>
                  <SelectContent>
                    {MOTIFS_FIN_CONTRAT.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Commentaire</Label>
              <Input value={sortieData.commentaire} onChange={(e) => setSortieData({ ...sortieData, commentaire: e.target.value })} placeholder="Optionnel" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsSortieDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={submittingSortie || !sortieData.date_sortie || !sortieData.motif_fin_contrat}>
                {submittingSortie ? 'Enregistrement...' : 'Confirmer la sortie'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Aperçu de document (dossier salarié) */}
      <DocumentPreviewDialog
        preview={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={() => previewDoc?.sourceDoc && handleDownloadDocument(previewDoc.sourceDoc)}
      />
    </div>
  );
};

// DPAE Info Field Component - Champ copiable pour la modal DPAE
const DpaeInfoField = ({ label, value, onCopy, copied, className = '' }) => (
  <div className={`p-3 bg-slate-50 dark:bg-slate-800 rounded-lg ${className}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <p className="font-medium text-slate-900 dark:text-white">{value}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-slate-500 hover:text-[#0F5156]"
        onClick={onCopy}
      >
        {copied ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </Button>
    </div>
  </div>
);

// Document List Component
const EditDocumentDialog = ({ isOpen, onClose, doc, onSave, documentCategories }) => {
  if (!isOpen || !doc) return null;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Modifier le document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Titre</Label>
            <Input
              value={doc.titre}
              onChange={(e) => onSave({ ...doc, titre: e.target.value }, false)}
              placeholder="Titre du document"
            />
          </div>
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select value={doc.categorie} onValueChange={(value) => onSave({ ...doc, categorie: value }, false)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {documentCategories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={doc.description}
              onChange={(e) => onSave({ ...doc, description: e.target.value }, false)}
              placeholder="Description"
            />
          </div>
          <div className="space-y-2">
            <Label>Date du document</Label>
            <Input type="date" value={doc.date_document} onChange={(e) => onSave({ ...doc, date_document: e.target.value }, false)} />
          </div>
          <div className="space-y-2">
            <Label>Remplacer le fichier (optionnel)</Label>
            <Input type="file" onChange={(e) => onSave({ ...doc, newFile: e.target.files[0] }, false)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose(false)}>Annuler</Button>
            <Button onClick={() => onSave(doc, true)}>Enregistrer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DocumentList = ({ documents, onDelete, onEdit, onDownload, onPreview, emptyMessage }) => {
  if (documents.length === 0) {
    return <p className="text-center text-slate-500 py-4">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-2">
      {documents.map((doc) => (
        <Card key={doc.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#0F5156]" />
                <div>
                  <p className="font-medium text-sm">{doc.titre || doc.filename}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>Ajouté le {doc.uploaded_at ? formatDateShort(doc.uploaded_at) : '-'}</span>
                    {doc.date_document && <span>| Doc: {formatDateShort(doc.date_document)}</span>}
                    {doc.categorie && <Badge variant="outline" className="text-xs py-0">{doc.categorie}</Badge>}
                  </div>
                  {doc.description && (
                    <p className="text-xs text-slate-400 mt-1">{doc.description}</p>
                  )}
                  {doc.updated_at && (
                    <p className="text-xs text-blue-400 mt-0.5">Modifié le {formatDateShort(doc.updated_at)}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {onEdit && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:bg-blue-50" onClick={() => onEdit(doc)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[#0F5156] hover:bg-[#0F5156]/10" onClick={() => (onPreview ? onPreview(doc) : onDownload && onDownload(doc))} title="Aperçu">
                  <Eye className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDownload && onDownload(doc)} title="Télécharger">
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:bg-red-50"
                  onClick={() => onDelete(doc.id)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Employees;
